import React from "react";
import { SecondaryButton } from "@/components/Buttons.jsx";
import { isPublishJobTerminal } from "./usePublishJobTracker.js";

function jobTitle(job, isActive) {
  const status = String(job?.status || "").trim();
  if (!job) return "Starting publish batch…";
  if (status === "queued") return "Publish batch queued";
  if (status === "processing") return "Publishing drafts";
  if (status === "completed") return "Publish batch finished";
  if (status === "partial") return "Publish batch finished with errors";
  if (status === "failed") return "Publish batch failed";
  return isActive ? "Publishing drafts" : "Publish batch";
}

function jobSummary(job) {
  if (!job) return "Waiting for worker to pick up your drafts…";
  const total = Number(job.total) || 0;
  const succeeded = Number(job.succeeded) || 0;
  const failed = Number(job.failed) || 0;
  const pending = Math.max(0, Number(job.pending) || 0);
  const done = succeeded + failed;

  if (isPublishJobTerminal(job.status)) {
    if (failed === 0) {
      return `All ${succeeded} product${succeeded === 1 ? "" : "s"} published and linked to stores.`;
    }
    if (succeeded === 0) {
      return `None of the ${total} draft${total === 1 ? "" : "s"} could be published. Failed rows remain in the list.`;
    }
    return `${succeeded} published, ${failed} failed. Failed drafts stay in the list so you can fix and retry.`;
  }

  if (pending === total) {
    return `${total} draft${total === 1 ? "" : "s"} queued — creating products, uploading images, and linking merchants.`;
  }

  return `${done} of ${total} done${failed ? ` (${failed} failed)` : ""} — ${pending} remaining.`;
}

function progressPercent(job) {
  const total = Number(job?.total) || 0;
  if (total <= 0) return 0;
  const done = (Number(job.succeeded) || 0) + (Number(job.failed) || 0);
  return Math.min(100, Math.round((done / total) * 100));
}

function statusTone(job) {
  const status = String(job?.status || "").trim();
  if (status === "completed") {
    return "border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30";
  }
  if (status === "partial") {
    return "border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30";
  }
  if (status === "failed") {
    return "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30";
  }
  return "border-indigo-200 bg-indigo-50 dark:border-indigo-900/40 dark:bg-indigo-950/25";
}

function Stat({ label, value, tone = "slate" }) {
  const tones = {
    emerald: "text-emerald-700 dark:text-emerald-300",
    red: "text-red-700 dark:text-red-300",
    indigo: "text-indigo-700 dark:text-indigo-300",
    slate: "text-slate-700 dark:text-slate-300",
  };
  return (
    <div className="flex flex-col">
      <span className={`text-lg font-semibold tabular-nums leading-none ${tones[tone] || tones.slate}`}>
        {value}
      </span>
      <span className="text-xs text-slate-600 dark:text-slate-400 mt-1">{label}</span>
    </div>
  );
}

export default function BulkPublishProgressBanner({
  job,
  jobId,
  pollError,
  isActive,
  collapsed,
  onToggleCollapsed,
  onDismiss,
}) {
  if (!jobId) return null;

  const terminal = job ? isPublishJobTerminal(job.status) : false;
  const pct = progressPercent(job);
  const total = Number(job?.total) || 0;
  const succeeded = Number(job?.succeeded) || 0;
  const failed = Number(job?.failed) || 0;
  const pending = Math.max(0, Number(job?.pending) || (job ? total - succeeded - failed : total));

  if (collapsed && isActive) {
    return (
      <button
        type="button"
        onClick={onToggleCollapsed}
        className="w-full flex items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-left shadow-sm dark:border-indigo-900/40 dark:bg-indigo-950/25 hover:bg-indigo-100/80 dark:hover:bg-indigo-950/40 transition-colors"
      >
        <span className="inline-flex items-center gap-2 text-sm font-medium text-indigo-800 dark:text-indigo-200">
          <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-indigo-300 border-t-indigo-700 dark:border-indigo-600 dark:border-t-indigo-200" />
          Publishing drafts — {succeeded + failed} / {total || "…"}
        </span>
        <span className="text-xs text-indigo-600 dark:text-indigo-300 shrink-0">Show details</span>
      </button>
    );
  }

  return (
    <div
      className={`rounded-lg border px-4 py-4 shadow-sm ${statusTone(job)}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {isActive ? (
              <span
                className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-indigo-300 border-t-indigo-700 dark:border-indigo-600 dark:border-t-indigo-200"
                aria-hidden
              />
            ) : terminal && failed === 0 ? (
              <span className="text-emerald-600 dark:text-emerald-400" aria-hidden>
                ✓
              </span>
            ) : terminal ? (
              <span className="text-amber-600 dark:text-amber-400" aria-hidden>
                !
              </span>
            ) : null}
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {jobTitle(job, isActive)}
            </h3>
          </div>
          <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">{jobSummary(job)}</p>
          {pollError ? (
            <p className="mt-2 text-sm text-red-700 dark:text-red-300">{pollError}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isActive ? (
            <SecondaryButton type="button" onClick={onToggleCollapsed}>
              Minimize
            </SecondaryButton>
          ) : null}
          {terminal || pollError ? (
            <SecondaryButton type="button" onClick={onDismiss}>
              Dismiss
            </SecondaryButton>
          ) : null}
        </div>
      </div>

      {total > 0 ? (
        <div className="mt-4 space-y-3">
          <div>
            <div className="mb-1 flex justify-between text-xs text-slate-600 dark:text-slate-400">
              <span>Progress</span>
              <span className="tabular-nums font-medium">
                {succeeded + failed} / {total} ({pct}%)
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/70 dark:bg-slate-900/40">
              <div
                className={`h-full rounded-full transition-all duration-500 ease-out ${
                  terminal && failed > 0
                    ? "bg-amber-500"
                    : terminal
                      ? "bg-emerald-500"
                      : "bg-indigo-500"
                }`}
                style={{ width: `${Math.max(isActive && pct === 0 ? 8 : pct, terminal ? pct : pct || 8)}%` }}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-6 sm:gap-10">
            <Stat label="Published" value={succeeded} tone="emerald" />
            <Stat label="Failed" value={failed} tone={failed > 0 ? "red" : "slate"} />
            <Stat
              label={isActive ? "In progress" : "Remaining"}
              value={pending}
              tone={isActive ? "indigo" : "slate"}
            />
            <Stat label="Total" value={total} />
          </div>
        </div>
      ) : null}

      {isActive ? (
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          You can keep working — this list refreshes automatically as each draft publishes.
        </p>
      ) : null}
    </div>
  );
}
